import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CheckCircle, XCircle, Clock, Info } from "lucide-react";
import { VIDEO_CALL_END_REASONS } from "@/constants/video-call";

interface InterviewEndedDialogProps {
  open: boolean;
  reason?: string;
  onClose: () => void;
  networkDisconnected?: boolean;
}

export const InterviewEndedDialog: React.FC<InterviewEndedDialogProps> = ({
  open,
  reason = "Interview completed",
  onClose,
  networkDisconnected = false,
}) => {
  // Determine icon and styling based on reason
  const getReasonDetails = () => {
    // If network was disconnected and reason is assistant-ended, treat it as network lost
    if (networkDisconnected && (reason === VIDEO_CALL_END_REASONS.ASSISTANT_ENDED || reason === 'network-connection-lost')) {
      return {
        icon: <Info className="w-12 h-12 text-gray-600" />,
        title: "Network Connection Lost",
        description: "The interview ended due to network connection loss. You will be redirected to the previous page.",
        bgColor: "bg-gray-50",
        borderColor: "border-gray-200",
      };
    }

    // Check exact ending reason constants first
    switch (reason) {
      // Assistant/System completions
      case VIDEO_CALL_END_REASONS.ASSISTANT_ENDED:
      case VIDEO_CALL_END_REASONS.INTERVIEW_COMPLETED:
        return {
          icon: <CheckCircle className="w-12 h-12 text-green-600" />,
          title: "Interview Complete",
          description: "Thank you for your time. The interview has been completed successfully.",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
        };

      // User endings
      case VIDEO_CALL_END_REASONS.USER_ENDED:
      case VIDEO_CALL_END_REASONS.USER_ENDED_DURING_RING:
        return {
          icon: <Info className="w-12 h-12 text-blue-600" />,
          title: "You Left the Interview",
          description: "You have exited the interview session. Thank you for your participation.",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
        };

      // Time limit
      case VIDEO_CALL_END_REASONS.TIME_LIMIT_REACHED:
        return {
          icon: <Clock className="w-12 h-12 text-orange-600" />,
          title: "Time's Up",
          description: "The allocated time for this interview has expired. Thank you for your participation.",
          bgColor: "bg-orange-50",
          borderColor: "border-orange-200",
        };

      // Violation-based endings
      case VIDEO_CALL_END_REASONS.TAB_CHANGE_VIOLATION:
        return {
          icon: <XCircle className="w-12 h-12 text-red-600" />,
          title: "Interview Terminated",
          description: "The interview has been terminated due to excessive tab switching. Please contact the recruiter if you believe this was in error.",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };

      case VIDEO_CALL_END_REASONS.WINDOW_BLUR_VIOLATION:
        return {
          icon: <XCircle className="w-12 h-12 text-red-600" />,
          title: "Interview Terminated", 
          description: "The interview has been terminated due to leaving the interview window multiple times. Please contact the recruiter if you believe this was in error.",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };

      case VIDEO_CALL_END_REASONS.MONITOR_VIOLATION:
        return {
          icon: <XCircle className="w-12 h-12 text-red-600" />,
          title: "Interview Terminated",
          description: "The interview has been terminated due to external monitor detection. Please contact the recruiter if you believe this was in error.",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };

      case VIDEO_CALL_END_REASONS.CAMERA_VIOLATION:
      case VIDEO_CALL_END_REASONS.CAMERA_FAILED:
        return {
          icon: <XCircle className="w-12 h-12 text-red-600" />,
          title: "Interview Terminated",
          description: "The interview has been terminated due to camera disconnection. Please ensure your camera is working and contact the recruiter if needed.",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };

      case VIDEO_CALL_END_REASONS.SCREENSHARE_VIOLATION:
      case VIDEO_CALL_END_REASONS.SCREENSHARE_FAILED:
        return {
          icon: <XCircle className="w-12 h-12 text-red-600" />,
          title: "Interview Terminated",
          description: "The interview has been terminated due to screen sharing issues. Please ensure you're sharing your full screen and contact the recruiter if needed.",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };

      case VIDEO_CALL_END_REASONS.FACE_DETECTION_VIOLATION:
        return {
          icon: <XCircle className="w-12 h-12 text-red-600" />,
          title: "Interview Terminated",
          description: "The interview has been terminated due to face detection issues. Please ensure you remain visible to the camera throughout the interview.",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };

      // Technical/Network issues
      case VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST:
        return {
          icon: <Info className="w-12 h-12 text-gray-600" />,
          title: "Network Connection Lost",
          description: "The interview ended due to network connection loss. You will be redirected to the previous page.",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
        };

      case VIDEO_CALL_END_REASONS.NETWORK_DISCONNECTED:
      case VIDEO_CALL_END_REASONS.CONNECTION_FAILED:
      case VIDEO_CALL_END_REASONS.TECHNICAL_ISSUES:
        return {
          icon: <Info className="w-12 h-12 text-gray-600" />,
          title: "Connection Issues",
          description: "The interview ended due to technical difficulties. Please contact the recruiter to reschedule.",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
        };

      case VIDEO_CALL_END_REASONS.SERVER_SHUTDOWN:
      case VIDEO_CALL_END_REASONS.ROOM_DELETED:
      case VIDEO_CALL_END_REASONS.PARTICIPANT_REMOVED:
        return {
          icon: <Info className="w-12 h-12 text-gray-600" />,
          title: "Interview Session Ended",
          description: "The interview session has ended. Thank you for your time.",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
        };

      // Media failures
      case VIDEO_CALL_END_REASONS.AUDIO_FAILED:
        return {
          icon: <XCircle className="w-12 h-12 text-red-600" />,
          title: "Audio Issues",
          description: "The interview ended due to audio problems. Please check your microphone and contact the recruiter to reschedule.",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };

      // Default/Unknown
      case VIDEO_CALL_END_REASONS.UNKNOWN:
      default:
        // Fallback to legacy string matching for compatibility
        const lowerReason = reason.toLowerCase();
        
        if (lowerReason.includes("completed") || lowerReason.includes("finished") || 
            lowerReason.includes("thank you") || lowerReason.includes("assistant-ended") ||
            lowerReason.includes("interview complete") || lowerReason.includes("agent ending")) {
          return {
            icon: <CheckCircle className="w-12 h-12 text-green-600" />,
            title: "Interview Complete",
            description: "Thank you for your time. The interview has been completed.",
            bgColor: "bg-green-50",
            borderColor: "border-green-200",
          };
        }
        
        if (lowerReason.includes("user ended") || lowerReason.includes("candidate ended")) {
          return {
            icon: <Info className="w-12 h-12 text-blue-600" />,
            title: "You Left the Interview",
            description: "You have exited the interview session. Thank you for your participation.",
            bgColor: "bg-blue-50",
            borderColor: "border-blue-200",
          };
        }
        
        if (lowerReason.includes("violation") || lowerReason.includes("limit reached")) {
          return {
            icon: <XCircle className="w-12 h-12 text-red-600" />,
            title: "Interview Terminated",
            description: "The interview has been terminated due to monitoring violations. Please contact the recruiter if you believe this was in error.",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
          };
        }
        
        return {
          icon: <Info className="w-12 h-12 text-blue-600" />,
          title: "Interview Ended",
          description: "The interview session has concluded. Thank you for your participation.",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
        };
    }
  };

  const details = getReasonDetails();

  const handleClose = () => {
    onClose();
  };

  return (
    <AlertDialog open={open} onOpenChange={() => {}}>
      <AlertDialogContent className="max-w-md">
        <div className={`p-6 rounded-lg ${details.bgColor} border ${details.borderColor} mb-4`}>
          <div className="flex flex-col items-center text-center">
            {details.icon}
            <AlertDialogHeader className="mt-4">
              <AlertDialogTitle className="text-xl font-semibold">
                {details.title}
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-2 text-gray-700">
                {details.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
        </div>
        
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={handleClose}
            className="w-full sm:w-auto"
          >
            OK
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};